// POS → Restaurant → Recetas y Food Cost (/pos/rest-recetas).
// Escandallo: cada plato (producto SIN control de stock) define sus ingredientes
// del inventario; al COBRAR la cuenta se descargan del kardex. El food cost
// compara costo de receta (costo promedio del local) vs precio del plato.
import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CookingPot, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { SelectorLocal, Campo, fieldCls, money, posRpc } from './shared';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { FoodCostT, RecetaItemT, useLocalRestaurant } from './restaurante';

const INV = { headers: { 'Content-Profile': 'inventario', 'Accept-Profile': 'inventario' } };

interface ProductoLite { id: string; codigo: string; nombre: string; precio_base: number; controla_stock: boolean; unidad_medida?: string }

function BuscadorProducto({ locationId, placeholder, onPick }: {
  locationId: string; placeholder: string; onPick: (p: ProductoLite) => void;
}) {
  const [q, setQ] = useState('');
  const { data: rows = [] } = useQuery<ProductoLite[]>({
    queryKey: ['rest-fc-buscar', q, locationId],
    queryFn: async () => (await apiClient.post('/rpc/pos_buscar_productos',
      { p_q: q, p_location: locationId || null, p_partner: null }, INV)).data,
    enabled: q.length >= 2,
  });
  return (
    <div className="relative">
      <input className={fieldCls} placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} />
      {q.length >= 2 && rows.length > 0 && (
        <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto border rounded-md bg-card shadow divide-y">
          {rows.map((p) => (
            <button key={p.id} className="w-full text-left px-3 py-1.5 text-sm flex justify-between gap-2 hover:bg-muted"
                    onClick={() => { onPick(p); setQ(''); }}>
              <span>{p.nombre} <span className="text-xs text-muted-foreground">{p.codigo}</span></span>
              <span className="font-mono">{money(p.precio_base)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function RecetasRestaurant() {
  const qc = useQueryClient();
  const { locationId, setLocationId, locations } = useLocalRestaurant();
  const [plato, setPlato] = useState<ProductoLite | null>(null);
  const [items, setItems] = useState<RecetaItemT[]>([]);

  const { data: foodcost = [] } = useQuery<FoodCostT[]>({
    queryKey: ['rest-foodcost', locationId],
    queryFn: () => posRpc<FoodCostT[]>('rest_reporte_foodcost', { _location: locationId }),
    enabled: !!locationId,
    staleTime: 0, refetchOnMount: 'always',
  });

  const cargarReceta = async (p: ProductoLite) => {
    setPlato(p);
    try {
      const r = await posRpc<{ items: RecetaItemT[] }>('rest_receta_get', { _plato: p.id, _location: locationId });
      setItems(r.items.map((i) => ({ ...i, cantidad: Number(i.cantidad) })));
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const guardar = async () => {
    if (!plato) return;
    try {
      await posRpc('rest_receta_set', {
        _plato: plato.id,
        _items: items.filter((i) => i.cantidad > 0)
          .map((i) => ({ ingrediente_id: i.ingrediente_id, cantidad: i.cantidad })),
      });
      void qc.invalidateQueries({ queryKey: ['rest-foodcost'] });
      toast.success(`Receta de «${plato.nombre}» guardada`);
      void cargarReceta(plato);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const costoTotal = items.reduce((a, i) => a + (Number(i.costo_unitario) || 0) * (Number(i.cantidad) || 0), 0);
  const fcPct = plato && plato.precio_base > 0 ? (costoTotal / plato.precio_base) * 100 : null;

  const getVal = useCallback((r: FoodCostT, k: string): string | number | null => {
    switch (k) {
      case 'nombre': return r.nombre;
      case 'precio': return Number(r.precio_base);
      case 'costo': return r.costo_receta == null ? null : Number(r.costo_receta);
      case 'fc': return r.foodcost_pct == null ? null : Number(r.foodcost_pct);
      case 'ing': return Number(r.ingredientes);
      default: return null;
    }
  }, []);
  const sort = useSort(foodcost, getVal);

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><CookingPot className="w-5 h-5" /> Recetas y Food Cost</h1>
        <SelectorLocal locationId={locationId} setLocationId={setLocationId} locations={locations} />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Editor de receta (escandallo)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <Campo label="Plato (producto sin control de stock)">
              <BuscadorProducto locationId={locationId} placeholder="Buscar plato…" onPick={(p) => void cargarReceta(p)} />
            </Campo>
            {plato && (
              <div className="text-sm self-end pb-1">
                <span className="font-medium">{plato.nombre}</span> · precio {money(plato.precio_base)}
                {plato.controla_stock && (
                  <span className="text-amber-600 dark:text-amber-400"> · ⚠ este producto controla stock propio: la receta NO descargará (apágalo en Inventario)</span>
                )}
              </div>
            )}
          </div>
          {plato && (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b">
                    <th className="py-2 pr-3 text-right w-10">N°</th>
                    <th className="py-2 pr-3 text-left">Ingrediente</th>
                    <th className="py-2 pr-3 text-right">Cantidad</th>
                    <th className="py-2 pr-3 text-right">Costo unit.</th>
                    <th className="py-2 pr-3 text-right">Costo</th>
                    <th className="py-2 pr-3" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((i, idx) => (
                    <tr key={i.ingrediente_id} className="border-b last:border-0">
                      <td className="py-1.5 pr-3 text-right text-muted-foreground">{idx + 1}</td>
                      <td className="py-1.5 pr-3">{i.nombre} <span className="text-xs text-muted-foreground">{i.unidad ?? ''}</span></td>
                      <td className="py-1.5 pr-3 text-right">
                        <input className={`${fieldCls} !w-24 text-right font-mono !h-7`} type="number" min="0" step="0.001"
                               value={i.cantidad}
                               onChange={(e) => setItems((xs) => xs.map((x) => x === i ? { ...x, cantidad: Number(e.target.value) } : x))} />
                      </td>
                      <td className="py-1.5 pr-3 text-right font-mono">{money(i.costo_unitario ?? 0)}</td>
                      <td className="py-1.5 pr-3 text-right font-mono">{money((i.costo_unitario ?? 0) * i.cantidad)}</td>
                      <td className="py-1.5 pr-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => setItems((xs) => xs.filter((x) => x !== i))}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {items.length > 0 && (
                    <tr className="border-t-2 font-semibold bg-muted/30">
                      <td className="py-2 pr-3" colSpan={4}>Costo de la receta ({items.length} ingredientes)</td>
                      <td className="py-2 pr-3 text-right font-mono">{money(costoTotal)}</td>
                      <td />
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="grid gap-3 md:grid-cols-2 items-end">
                <Campo label="Agregar ingrediente">
                  <BuscadorProducto locationId={locationId} placeholder="Buscar ingrediente…" onPick={(p) => {
                    if (p.id === plato.id) { toast.error('Un plato no puede ser su propio ingrediente'); return; }
                    setItems((xs) => xs.some((x) => x.ingrediente_id === p.id) ? xs
                      : [...xs, { ingrediente_id: p.id, nombre: p.nombre, codigo: p.codigo, cantidad: 1, costo_unitario: 0 }]);
                  }} />
                </Campo>
                <div className="flex items-center justify-between gap-3">
                  {fcPct != null && (
                    <span className={`text-sm font-medium ${fcPct > 35 ? 'text-red-600' : fcPct > 28 ? 'text-amber-600' : 'text-green-600'}`}>
                      Food cost: {fcPct.toFixed(1)}% {fcPct > 35 ? '(alto — el estándar es 28-35%)' : ''}
                    </span>
                  )}
                  <Button onClick={() => void guardar()}><Save className="w-4 h-4 mr-1" /> Guardar receta</Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Los ingredientes nuevos muestran costo $0 hasta guardar (el costo real es el promedio del local).
                El descargo del kardex ocurre al COBRAR la cuenta.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Food cost del menú</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b">
                <th className="py-2 pr-3 text-right w-10">N°</th>
                <SortTh col="nombre" sort={sort}>Plato</SortTh>
                <SortTh col="ing" sort={sort} align="right">Ingr.</SortTh>
                <SortTh col="precio" sort={sort} align="right">Precio</SortTh>
                <SortTh col="costo" sort={sort} align="right">Costo receta</SortTh>
                <SortTh col="fc" sort={sort} align="right">Food cost %</SortTh>
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((r, i) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{r.nombre} <span className="text-xs text-muted-foreground">{r.codigo}</span></td>
                  <td className="py-2 pr-3 text-right">{r.ingredientes}</td>
                  <td className="py-2 pr-3 text-right font-mono">{money(r.precio_base)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{r.costo_receta == null ? '—' : money(r.costo_receta)}</td>
                  <td className={`py-2 pr-3 text-right font-semibold ${r.foodcost_pct == null ? ''
                    : Number(r.foodcost_pct) > 35 ? 'text-red-600' : Number(r.foodcost_pct) > 28 ? 'text-amber-600' : 'text-green-600'}`}>
                    {r.foodcost_pct == null ? '—' : `${r.foodcost_pct}%`}
                  </td>
                </tr>
              ))}
              {!sort.sorted.length && (
                <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">
                  Aún no hay platos con receta. <Plus className="w-3.5 h-3.5 inline" /> Búscalo arriba y arma su escandallo.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
